import { z } from 'zod';
import { c } from '../../contract';
import { offerTierSchema } from '../../types/OfferTier';

const userSourceSchema = z.literal('deep-link').or(z.literal('organic'));

export const emailVerificationRouter = c.router(
  {
    sendVerification: {
      summary: 'Send email verification',
      method: 'POST',
      path: '/send-verification',
      body: z.object({
        userId: z.string(),
        languageCode: z.string().optional(),
        email: z.string().optional(),
        studentOffer: z.boolean().optional(),
        offering: z.string().optional(),
        productId: z.string().optional(),
        os: z.string().optional(),
        countryCode: z.string().optional(),
        storeCountryCode: z.string().optional(),
        userSource: userSourceSchema.optional(),
        isEligibleForAndroidDiscount: z.boolean().optional(),
        mode: z.enum(['user', 'admin']).optional(),
      }),
      responses: {
        404: z.object({
          reason: z.string(),
        }),
        401: z.object({
          reason: z.string(),
        }),
        200: z.discriminatedUnion('success', [
          z.object({
            success: z.literal(true),
          }),
          z.object({
            success: z.literal(false),
            reason: z.string(),
          }),
        ]),
      },
    },
    verify: {
      summary: 'Verify email',
      method: 'GET',
      path: '/verify',
      query: z.object({
        userId: z.string(),
        lang: z.string(),
        coupon: z.string().optional(),
        promocode: z.string().optional(),
        sku: z.string().optional(),
        isSubscriber: z.string().optional(),
        bundleId: z.string().optional(),
        /**
         * Application bundle id of the app that asked for the email, baked into the link when it is
         * sent. Not to be confused with `bundleId` above, which is a web-checkout bundle. The click
         * arrives with no session, so this is the only signal saying which brand is verifying — and
         * brands differ on whether verifying earns coins.
         */
        appBundleId: z.string().optional(),
        source: z.string().optional(),
        productId: z.string().optional(),
        os: z.string().optional(),
        countryCode: z.string().optional(),
        tracker: z.string().optional(),
        extraQueryParams: z.string().optional(),
        /**
         * Marketer-controlled discount tier, set per Iterable journey/template (e.g. `discount30` / `none`).
         * Resolved to a promocode server-side at click time, gated by a fail-closed subscriber veto.
         * Unknown values coerce to `none` so a tampered or stale link never breaks verification.
         */
        offer: offerTierSchema.optional().catch('none'),
        oobCode: z.string().optional(),
      }),
      responses: {
        302: z.void(),
      },
    },
    forceVerify: {
      summary: 'Verify email by admin',
      method: 'GET',
      path: '/force-verify',
      query: z.object({
        userId: z.string(),
        // Query values are strings; z.coerce.boolean() treats non-empty strings (including "false") as true.
        ignoreGdpr: z
          .string()
          .transform((v) => v === 'true')
          .optional(),
      }),
      responses: {
        404: z.object({
          reason: z.string(),
        }),
        200: z.discriminatedUnion('success', [
          z.object({
            success: z.literal(true),
          }),
          z.object({
            success: z.literal(false),
            reason: z.string(),
          }),
        ]),
      },
    },
    verificationStatus: {
      summary: 'Get email verification status',
      method: 'GET',
      path: '/verification-status',
      query: z.object({
        userId: z.string().optional(),
      }),
      responses: {
        404: z.object({
          reason: z.string(),
        }),
        401: z.object({
          reason: z.string(),
        }),
        200: z.discriminatedUnion('status', [
          z.object({
            status: z.literal('workflow-not-triggered'),
          }),
          z.object({
            status: z.literal('workflow-triggered'),
          }),
          z.object({
            status: z.literal('email-sent'),
            sentAt: z.string(),
          }),
          z.object({
            status: z.literal('attempted-verification'),
            attemptedAt: z.string(),
          }),
          z.object({
            status: z.literal('verified'),
            verifiedAt: z.string().optional(),
          }),
        ]),
      },
    },
  },
  { pathPrefix: '/email' },
);
